import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { CardImage } from "@/components/CardImage";
import { ConditionBadge, Tag } from "@/components/Badges";
import { CashFields, cashPayload, euro } from "@/components/CashFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const DELIVERY_LABEL = { shipping: "Spedizione", in_person: "Incontro di persona", "": "da concordare" };

// Reusable trade proposal composer with optional cash adjustment + delivery method.
export function ProposalComposer({ open, onOpenChange, toUserId, toUsername, onSent, presetGive = [], presetReceive = [] }) {
  const [mine, setMine] = useState([]);
  const [theirs, setTheirs] = useState([]);
  const [give, setGive] = useState({});
  const [receive, setReceive] = useState({});
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("telegram");
  const [value, setValue] = useState("");
  const [cash, setCash] = useState({ option: "none", amount: "" });
  const [delivery, setDelivery] = useState("");
  const [acceptsCash, setAcceptsCash] = useState(true);
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setGive({}); setReceive({}); setNote(""); setCash({ option: "none", amount: "" }); setDelivery("");
    api.get("/availabilities/mine").then((r) => setMine(r.data.filter((a) => a.available_for_trade && !a.card?.is_draft && a.available_qty > 0)));
    api.get(`/profiles/${toUsername}`).then((r) => { setTheirs(r.data.cards); setAcceptsCash(r.data.accept_cash_adjustment !== false); }).catch(() => setTheirs([]));
  }, [open, toUsername]);

  useEffect(() => {
    if (open && presetGive.length) setGive(Object.fromEntries(presetGive.map((id) => [id, 1])));
    if (open && presetReceive.length) setReceive(Object.fromEntries(presetReceive.map((id) => [id, 1])));
  }, [open]);

  const offered = useMemo(() => Object.entries(give).filter(([, q]) => q > 0), [give]);
  const requested = useMemo(() => Object.entries(receive).filter(([, q]) => q > 0), [receive]);
  const cp = cashPayload(cash);

  const submit = async (draft) => {
    if (!offered.length || !requested.length) { toast.error("Serve almeno una carta da offrire e una da richiedere."); return; }
    if (!draft && !value.trim()) { toast.error("Indica un recapito da condividere con la controparte."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/proposals", {
        to_user_id: toUserId,
        offered: offered.map(([availability_id, quantity]) => ({ availability_id, quantity: Number(quantity) })),
        requested: requested.map(([availability_id, quantity]) => ({ availability_id, quantity: Number(quantity) })),
        note, contact_method: method, contact_value: value || "-",
        ...cp, delivery_method: delivery || null, draft,
      });
      toast.success(draft ? "Bozza salvata." : "Proposta inviata!");
      onOpenChange(false);
      onSent?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const Row = ({ item, sel, setSel, testid }) => {
    const id = item.id;
    const checked = sel[id] > 0;
    const maxQ = item.available_qty ?? 1;
    return (
      <div className={`flex items-center gap-2 rounded-lg border p-2 ${checked ? "border-[#2DD4BF] bg-[#1C2640]" : "border-[#2A3655]"}`} data-testid={testid}>
        <input type="checkbox" checked={checked} onChange={(e) => setSel((s) => ({ ...s, [id]: e.target.checked ? 1 : 0 }))} className="h-4 w-4 accent-[#7C3AED]" />
        <div className="h-12 w-9 shrink-0"><CardImage path={(item.photos || [])[0]} card={item.card} className="h-full w-full" /></div>
        <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-[#F8FAFC]">{item.card?.name}</p><div className="flex gap-1"><ConditionBadge value={item.condition} /><Tag>{item.language}</Tag></div></div>
        {checked && maxQ > 1 && <Input type="number" min="1" max={maxQ} value={sel[id]} onChange={(e) => setSel((s) => ({ ...s, [id]: Math.min(maxQ, Number(e.target.value)) }))} className="w-16 bg-[#0B1020] border-[#2A3655]" />}
      </div>
    );
  };

  const cashSummary = cp.cash_cents ? (cash.option === "offerer_pays" ? `+ ${euro(cp.cash_cents)} (versi tu a @${toUsername})` : `+ ${euro(cp.cash_cents)} (li versa @${toUsername} a te)`) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC] sm:max-w-2xl" data-testid="proposal-composer">
        <DialogHeader><DialogTitle className="font-display">Proponi uno scambio a @{toUsername}</DialogTitle><DialogDescription className="sr-only">Seleziona le carte da offrire e richiedere, l'eventuale conguaglio e la consegna.</DialogDescription></DialogHeader>
        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-sm font-semibold text-[#2DD4BF]">TU OFFRI (le tue carte)</p>
              <div className="space-y-2">{mine.length === 0 ? <p className="text-sm text-[#94A3B8]">Nessuna carta disponibile da offrire.</p> : mine.map((a) => <Row key={a.id} item={a} sel={give} setSel={setGive} testid="give-option" />)}</div>
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-[#7C3AED]">TU CHIEDI (le sue carte)</p>
              <div className="space-y-2">{theirs.length === 0 ? <p className="text-sm text-[#94A3B8]">Nessuna carta pubblica disponibile.</p> : theirs.map((a) => <Row key={a.id} item={a} sel={receive} setSel={setReceive} testid="receive-option" />)}</div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <CashFields cash={cash} setCash={setCash} delivery={delivery} setDelivery={setDelivery} disabledCash={!acceptsCash} />
            <div><Label className="text-[#CBD5E1]">Nota (facoltativa)</Label><Textarea data-testid="proposal-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" /></div>
            <div className="grid grid-cols-[160px_1fr] gap-2">
              <div><Label className="text-[#CBD5E1]">Recapito</Label>
                <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="contact-method" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]"><SelectItem value="telegram">Username Telegram</SelectItem><SelectItem value="email">Email di contatto</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-[#CBD5E1]">{method === "telegram" ? "Il tuo username Telegram" : "La tua email di contatto"}</Label><Input data-testid="contact-value" value={value} onChange={(e) => setValue(e.target.value)} className="bg-[#0B1020] border-[#2A3655]" /></div>
            </div>
            <div className="rounded-lg border border-[#2A3655] bg-[#0B1020] p-3 text-sm text-[#CBD5E1]" data-testid="proposal-summary">
              <p className="font-semibold text-[#F8FAFC]">Riepilogo</p>
              <p className="mt-1">@{"tu"} offri <b className="text-[#2DD4BF]">{offered.length}</b> {offered.length === 1 ? "carta" : "carte"} {cashSummary && <span className="text-[#F59E0B]">{cashSummary}</span>}</p>
              <p>@{toUsername} offre <b className="text-[#7C3AED]">{requested.length}</b> {requested.length === 1 ? "carta" : "carte"}</p>
              <p className="mt-1 text-xs text-[#94A3B8]">Consegna proposta: {DELIVERY_LABEL[delivery]} · ogni partecipante paga il proprio invio. Il conguaglio è denaro in una sola direzione, mai al posto delle carte.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          {step === 1 ? (
            <Button onClick={() => { if (!offered.length || !requested.length) { toast.error("Seleziona almeno una carta per parte."); return; } setStep(2); }} data-testid="proposal-next" className="bg-[#7C3AED] hover:bg-[#6D28D9]">Continua</Button>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-[#CBD5E1]">Indietro</Button>
              <Button variant="outline" onClick={() => submit(true)} disabled={sending} data-testid="proposal-draft" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]">Salva bozza</Button>
              <Button onClick={() => submit(false)} disabled={sending} data-testid="proposal-send" className="bg-[#7C3AED] hover:bg-[#6D28D9]">{sending ? "Invio…" : "Invia proposta"}</Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
